import { useProjects, useUpdateProject } from '@consigli/hooks';
import { Project } from '@consigli/types';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  ModalAlert,
  DataTable,
  ObjectDetailView,
  Pagination,
  DEFAULT_PAGE_SIZE,
} from '@/components';
import { errorToast, successToast } from '@/components/toast';
import { EditProjectForm } from '@/pages/project/edit-project-form';
import { BackofficeRoutes } from '@/route-names';

import { createProjectColumns } from './columns';
import { useDeleteProject } from './use-delete-project';

import type { ProjectFilters } from '../types';

interface ProjectsTableProps {
  page: number;
  setPage: (page: number) => void;
  debouncedSearchTerm: string;
  filters?: ProjectFilters;
}

export const ProjectsTable = ({
  page,
  setPage,
  debouncedSearchTerm,
  filters,
}: ProjectsTableProps) => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false);
  const [editProject, setEditProject] = useState<Project | null>(null);

  const { projects, paginatedCount, isFetching } = useProjects({
    page: page,
    search: debouncedSearchTerm,
    isHidden: true,
    packageTypes: filters?.packageTypes?.length ? filters.packageTypes.join(',') : undefined,
    organization: filters?.organization?.length
      ? filters.organization.map((org) => org.id).join(',')
      : undefined,
  });

  // Delete project functionality
  const {
    isModalOpen,
    projectToDelete,
    isDeleting,
    handleDeleteClick,
    handleConfirmDelete,
    handleCloseModal,
  } = useDeleteProject();

  // Toggle visibility functionality
  const { updateProject, isLoading: isTogglingVisibility } = useUpdateProject();

  const handleToggleVisibilityClick = useCallback(
    async (project: Project) => {
      try {
        await updateProject({
          projectId: project.id,
          data: { isHidden: !project.isHidden },
        }).unwrap();
        successToast({
          message: project.isHidden
            ? `Project "${project.name}" is now public`
            : `Project "${project.name}" is now hidden`,
        });
      } catch (error) {
        console.error('Failed to update project visibility:', error);
        errorToast({ message: 'Failed to update project visibility' });
      }
    },
    [updateProject],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage],
  );

  const handleRowClick = useCallback(
    (project: Project) => {
      navigate(`/${BackofficeRoutes.PROJECTS}/${project.id}`);
    },
    [navigate],
  );

  const handleViewDetailsClick = useCallback((project: Project) => {
    setSelectedProject(project);
    setIsDetailViewOpen(true);
  }, []);

  const handleEditServicesClick = useCallback((project: Project) => {
    setEditProject(project);
  }, []);

  const columns = createProjectColumns({
    onDeleteClick: handleDeleteClick,
    onViewDetailsClick: handleViewDetailsClick,
    onToggleVisibilityClick: handleToggleVisibilityClick,
    onEditServicesClick: handleEditServicesClick,
    isTogglingVisibility,
    isDeleting,
  });

  return (
    <div className="flex flex-col h-full">
      <DataTable
        columns={columns}
        data={projects}
        isLoading={isFetching}
        onRowClick={handleRowClick}
        scrollable={true}
        height="70vh"
      />
      <div className="flex justify-center my-4 flex-shrink-0">
        <Pagination
          onPageChange={handlePageChange}
          pageSize={DEFAULT_PAGE_SIZE}
          currentPage={page}
          totalCount={paginatedCount}
        />
      </div>

      <ModalAlert
        isOpen={isModalOpen}
        onOpenChange={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title="Delete Project"
        description={
          projectToDelete
            ? `Are you sure you want to delete the project "${projectToDelete.name}"? This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
        loadingText="Deleting..."
        destructive={true}
      />

      {selectedProject && (
        <ObjectDetailView
          object={selectedProject}
          objectType="Project"
          summary={`Project "${selectedProject.name}" details including organization, services, and configuration.`}
          isOpen={isDetailViewOpen}
          onOpenChange={(open: boolean) => {
            setIsDetailViewOpen(open);
            if (!open) {
              setSelectedProject(null);
            }
          }}
        />
      )}

      {editProject && (
        <EditProjectForm
          project={editProject}
          open={!!editProject}
          onOpenChange={(open) => {
            if (!open) {
              setEditProject(null);
            }
          }}
        />
      )}
    </div>
  );
};
